import { useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/App";
import { Loader2, ArrowLeft } from "lucide-react";

interface Organization {
  id: string;
  name: string;
}

export default function BusinessUnitCreate() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    organizationId: user?.organizationId || "",
    cardProgramName: "",
    maxCardsPerCustomer: 5,
    status: "ACTIVE",
  });

  const { data: organizations } = useQuery<Organization[]>({
    queryKey: ["organizations"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/organizations");
      if (!response.ok) throw new Error("Failed to fetch program managers");
      return response.json();
    },
    enabled: user?.role === "SUPER",
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiRequest("POST", "/api/business-units", data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create business unit");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-units"] });
      toast({ title: "Success", description: "Business Unit created successfully" });
      setLocation("/business-unit/view");
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => setLocation("/business-unit/view")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Create Business Unit</h1>
          <p className="text-muted-foreground">Add a new business unit</p>
        </div>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Business Unit Details</CardTitle>
          <CardDescription>Enter the details for the new business unit</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {user?.role === "SUPER" && (
              <div className="space-y-2">
                <Label htmlFor="organizationId">Organization *</Label>
                <Select
                  value={formData.organizationId}
                  onValueChange={(v) => setFormData({ ...formData, organizationId: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations?.map((pm) => (
                      <SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Organization Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Tech Corp Australia"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="admin@corp.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+61 400 000 000"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cardProgramName">Card Program Name</Label>
              <Input
                id="cardProgramName"
                value={formData.cardProgramName}
                onChange={(e) => setFormData({ ...formData, cardProgramName: e.target.value })}
                placeholder="Employee Expense Cards"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxCardsPerCustomer">Max Cards Per Customer</Label>
              <Input
                id="maxCardsPerCustomer"
                type="number"
                value={formData.maxCardsPerCustomer}
                onChange={(e) => setFormData({ ...formData, maxCardsPerCustomer: parseInt(e.target.value) })}
                min={1}
                max={10}
              />
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Business Unit
              </Button>
              <Button type="button" variant="outline" onClick={() => setLocation("/business-unit/view")}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
